import { cn } from '@thingslog/ui-components';
import { useMediaQuery } from '@mui/material';
import React, { FC, useCallback, useEffect, useRef, useState } from 'react';

interface Props {
  open: boolean;
  className?: string;
  children: React.ReactNode;
}

const DEFAULT_SIDEBAR_WIDTH_PX = 512;
const MIN_SIDEBAR_WIDTH_PX = 384;
const SIDEBAR_WIDTH_KEYBOARD_STEP_PX = 32;
const SIDEBAR_WIDTH_STORAGE_KEY = 'dashboard-widget-sidebar-width';

const getMaxSidebarWidth = (): number => {
  return Math.max(MIN_SIDEBAR_WIDTH_PX, Math.floor(window.innerWidth * 0.8));
};

const clampSidebarWidth = (width: number): number => {
  return Math.min(Math.max(width, MIN_SIDEBAR_WIDTH_PX), getMaxSidebarWidth());
};

const getInitialSidebarWidth = (): number => {
  const storedWidth = Number(window.localStorage.getItem(SIDEBAR_WIDTH_STORAGE_KEY));

  if (!Number.isFinite(storedWidth) || storedWidth <= 0) {
    return DEFAULT_SIDEBAR_WIDTH_PX;
  }

  return clampSidebarWidth(storedWidth);
};

export const ResizableDashboardWidgetSidebarContainer: FC<Props> = ({
  open,
  className,
  children,
}: Props) => {
  const isMobile = useMediaQuery('(max-width:767px)');
  const [sidebarWidth, setSidebarWidth] = useState(getInitialSidebarWidth);
  const [isResizing, setIsResizing] = useState(false);
  const resizeStartRef = useRef<{ pointerX: number; width: number }>({
    pointerX: 0,
    width: DEFAULT_SIDEBAR_WIDTH_PX,
  });
  const resizeAnimationFrameRef = useRef<number | null>(null);

  const handleResizeStart = useCallback(
    (event: React.PointerEvent<HTMLDivElement>): void => {
      event.preventDefault();
      resizeStartRef.current = {
        pointerX: event.clientX,
        width: sidebarWidth,
      };
      setIsResizing(true);
    },
    [sidebarWidth]
  );

  const handleResizeKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      setSidebarWidth((currentWidth: number): number => {
        return clampSidebarWidth(currentWidth + SIDEBAR_WIDTH_KEYBOARD_STEP_PX);
      });
    }

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      setSidebarWidth((currentWidth: number): number => {
        return clampSidebarWidth(currentWidth - SIDEBAR_WIDTH_KEYBOARD_STEP_PX);
      });
    }
  };

  useEffect(() => {
    if (!isResizing) {
      return;
    }

    const handlePointerMove = (event: PointerEvent): void => {
      const delta = resizeStartRef.current.pointerX - event.clientX;
      setSidebarWidth(clampSidebarWidth(resizeStartRef.current.width + delta));
    };

    const handlePointerUp = (): void => {
      setIsResizing(false);
    };

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);

    return () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
  }, [isResizing]);

  useEffect(() => {
    const handleWindowResize = (): void => {
      setSidebarWidth((currentWidth: number): number => clampSidebarWidth(currentWidth));
    };

    window.addEventListener('resize', handleWindowResize);

    return () => {
      window.removeEventListener('resize', handleWindowResize);
    };
  }, []);

  useEffect(() => {
    window.localStorage.setItem(SIDEBAR_WIDTH_STORAGE_KEY, sidebarWidth.toString());
  }, [sidebarWidth]);

  useEffect(() => {
    if (resizeAnimationFrameRef.current !== null) {
      cancelAnimationFrame(resizeAnimationFrameRef.current);
    }

    resizeAnimationFrameRef.current = requestAnimationFrame(() => {
      window.dispatchEvent(new Event('resize'));
      resizeAnimationFrameRef.current = null;
    });

    return () => {
      if (resizeAnimationFrameRef.current !== null) {
        cancelAnimationFrame(resizeAnimationFrameRef.current);
        resizeAnimationFrameRef.current = null;
      }
    };
  }, [open, sidebarWidth]);

  return (
    <aside
      style={
        open
          ? isMobile
            ? { width: '100%', minWidth: 0 }
            : { width: sidebarWidth, minWidth: sidebarWidth }
          : undefined
      }
      className={cn(
        className,
        open
          ? 'fixed inset-x-0 bottom-16 top-0 z-40 flex min-w-0 flex-col gap-2 overflow-y-auto border-0 border-l border-solid border-gray-200 bg-gradient-to-b from-gray-100 to-white p-2 md:relative md:inset-auto md:bottom-auto md:top-auto md:z-auto md:h-full'
          : 'hidden'
      )}
    >
      {!isMobile && (
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize dashboard sidebar"
          aria-valuemin={MIN_SIDEBAR_WIDTH_PX}
          aria-valuemax={getMaxSidebarWidth()}
          aria-valuenow={sidebarWidth}
          tabIndex={0}
          className={cn(
            'group absolute left-0 top-0 z-20 flex h-full w-6 cursor-col-resize items-stretch justify-start outline-none',
            isResizing && 'bg-cyan-500/10'
          )}
          onPointerDown={handleResizeStart}
          onKeyDown={handleResizeKeyDown}
        >
          <span
            className={cn(
              'h-full w-1 bg-transparent transition-colors group-hover:bg-cyan-500/50 group-focus:bg-cyan-500/60',
              isResizing && 'bg-cyan-500/70'
            )}
          />
        </div>
      )}
      {children}
    </aside>
  );
};
